import { z, type ZodType } from 'zod'
import { API_ERROR_CODE, apiErrorBodySchema, type ApiErrorBody } from '@lib/dto/api-error.dto'
import { loginResponseDtoSchema } from '@lib/dto/auth.dto'
import { API_VERSION_PREFIX } from '@lib/constants/api-version'
import { SHARE_TOKEN_PREFIX } from '@lib/constants/public-schedule'
import { tokenStorage } from './tokenStorage'
import { sessionEvents } from './sessionEvents'
import logger, { toErrorMessage } from './logger'
import { appIsOutdated } from './versionClient'

/**
 * The fetch-backed HTTP client for the web app. Every response is validated
 * with a Zod schema at the network boundary — the client never launders a raw
 * `.json()` payload into a concrete type (`app/no-untyped-fetch-json`). The
 * schema is both the validator and the source of the returned type.
 *
 * Usage:
 *   const venue = await api.get('/venues/1', venueDtoSchema) // VenueDto
 *   const maybe = await api.getNullable('/venues/1', venueDtoSchema) // VenueDto | null
 *
 * `get<T>` asserts the endpoint always returns a `T`. For endpoints that can
 * legitimately return no content (empty body / 204 / a bare `null`), use
 * `getNullable<T>`, which resolves an absent body to `null` and types the
 * result `T | null` (`app/prefer-get-nullable`).
 */

/** The base URL of the API. Overridable per environment via `VITE_API_URL`. */
const API_ORIGIN = import.meta.env.VITE_API_URL ?? 'http://localhost:8000'

/**
 * Where a connected AI assistant reaches this account: the MCP endpoint, beside
 * the API and outside its versioning (the protocol fixes the path). Shown on the
 * profile page for people to paste into their assistant.
 */
export const ASSISTANT_CONNECTION_ADDRESS = `${API_ORIGIN.replace(/\/+$/, '')}/mcp`

/**
 * The version of the API this client speaks.
 *
 * Part of the client rather than of `VITE_API_URL`, because it is a fact about
 * this code and not about the environment it runs in: a build that speaks v1
 * speaks v1 everywhere, and moving to v2 should be a change someone reviews
 * rather than an environment variable someone edits in a dashboard.
 */
const API_BASE_URL = `${API_ORIGIN}/${API_VERSION_PREFIX}`

export type { ApiErrorBody }

/**
 * Thrown for any non-2xx response (and for network failures, with `status: 0`).
 * Carries the HTTP status and the parsed error body so callers and React Query
 * error states can branch on `status` or surface `body.message`.
 */
export class ApiError extends Error {
  readonly status: number
  readonly body: ApiErrorBody

  constructor(status: number, body: ApiErrorBody) {
    super(body.message ?? body.error ?? `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

/** Type guard for {@link ApiError} that narrows without an `unknown` annotation. */
export function isApiError<E>(error: E): error is E & ApiError {
  return error instanceof ApiError
}

/**
 * Thrown when a response is valid JSON but not the shape this build expects.
 *
 * In practice that means this tab's JS is older than the API it is talking to,
 * which is ordinary for a few minutes after a deploy and permanent for a tab
 * left open. The user-facing message therefore names the fix — reload — rather
 * than reporting a validation failure they cannot act on. Response schemas
 * tolerate unknown keys, so reaching here means a field is missing or the wrong
 * type, not merely that the server knows something new.
 */
export class ResponseShapeError extends Error {
  /** Compact `path: code` summary for logs. Never the payload — that is user data. */
  readonly detail: string

  constructor(detail: string) {
    super('This page is running an older version of the app. Reload to continue.')
    this.name = 'ResponseShapeError'
    this.detail = detail
  }
}

/** Type guard for {@link ResponseShapeError}. */
export function isResponseShapeError<E>(error: E): error is E & ResponseShapeError {
  return error instanceof ResponseShapeError
}

/**
 * Summarize why a body failed its schema, as `path: code` pairs. Deliberately
 * drops Zod's messages and received values: those quote the payload, which then
 * rides along into logs and Sentry.
 */
function summarizeIssues(error: z.ZodError): string {
  const parts = error.issues.slice(0, 5).map((issue) => {
    const path = issue.path.map((p) => String(p)).join('.')
    return `${path === '' ? '(root)' : path}: ${issue.code}`
  })
  const extra = error.issues.length - parts.length
  return extra > 0 ? `${parts.join(', ')} (+${String(extra)} more)` : parts.join(', ')
}

/**
 * Validate a response body. The single place a schema meets the network, so the
 * "your tab is old" diagnosis is made once instead of per call site.
 */
function validate<T>(schema: ZodType<T>, value: unknown, method: string, path: string): T {
  const result = schema.safeParse(value)
  if (result.success) {
    return result.data
  }
  const detail = summarizeIssues(result.error)
  // A tab running older JS than what is deployed will fail schemas whenever a
  // field has since changed — `eventTrackId` being removed, `date` becoming a
  // calendar day. That is not a fault, it is the reason the message tells the
  // person to reload, and a tab left open across a few deploys files one of
  // these for every request it makes. When the tab IS current, the same failure
  // means the server sent something no client can read, which is a real bug and
  // stays loud: it is how the unsigned storage keys were finally caught.
  if (appIsOutdated()) {
    logger.warn('Response did not match the expected shape (older tab)', { method, path, detail })
  } else {
    logger.error('Response did not match the expected shape', { method, path, detail })
  }
  throw new ResponseShapeError(detail)
}

/**
 * What to tell a person when the browser never got an answer.
 *
 * `status: 0` carries the browser's own words ("Failed to fetch"), which name
 * nothing they can do. The form or the Try again button is still there.
 */
const UNANSWERED_REQUEST = 'That didn’t go through. Check your connection and try again.'

/**
 * Turn any caught value into a message fit to show a user: the API's own error
 * message for an {@link ApiError} (falling back to its status), otherwise a
 * generic normalized message. The one place UI derives an error string —
 * prefer this over re-deriving it per page.
 *
 * A request that never got an answer is the exception: the browser's message
 * is replaced with {@link UNANSWERED_REQUEST}. A 4xx or 5xx still shows what
 * the server said.
 */
export function requestErrorMessage<E>(error: E): string {
  if (isApiError(error)) {
    if (error.status === 0) return UNANSWERED_REQUEST
    return error.body.message ?? `Request failed (status ${error.status}).`
  }
  return toErrorMessage(error)
}

/** Options accepted by every request method. */
export interface RequestOptions {
  /** Extra headers merged over the defaults. */
  headers?: Record<string, string>
  /** AbortController signal for cancellation. */
  signal?: AbortSignal
  /**
   * Send no `Authorization` header, and do not treat a 401 as a lapsed session.
   *
   * For the public schedule, whose routes take a token in the path and have no
   * auth guard at all. A signed-in venue manager checking what their crew sees
   * would otherwise hand a live credential to a route that has no use for one,
   * and — worse — a 401 from it would clear their session.
   */
  anonymous?: boolean
}

/**
 * The path as it is safe to log.
 *
 * An anonymous request carries its authority in the URL — that is what makes it
 * anonymous — so its path is a secret. Both failure logs below are `logger.warn`,
 * which leaves a Sentry breadcrumb carrying whatever context it is given, and a
 * stale bookmark fails often enough that a live share link would end up in a
 * third-party log within a day of shipping.
 */
function loggablePath(path: string): string {
  return path
    .split('/')
    .map((segment) => (segment.startsWith(SHARE_TOKEN_PREFIX) ? `${SHARE_TOKEN_PREFIX}\u2026` : segment))
    .join('/')
}

/** A JSON-serializable request body — the call site supplies its concrete shape. */
export type RequestBody = object

/**
 * Parse a text body as JSON, treating an empty body as `null`. Returns
 * `unknown` so callers must validate (typically via `schema.parse`) before use.
 */
function parseJson(text: string): unknown {
  if (text.trim() === '') return null
  // JSON.parse is `any` in lib.dom; reassert as unknown at the boundary.
  return JSON.parse(text) as unknown
}

/** Extract a typed error body from a raw response text (never throws). */
function readErrorBody(text: string): ApiErrorBody {
  if (text.trim() === '') return {}
  try {
    const result = apiErrorBodySchema.safeParse(JSON.parse(text))
    return result.success ? result.data : { message: text }
  } catch {
    return { message: text }
  }
}

/**
 * Endpoints that must never trigger the refresh-on-401 machinery: refreshing or
 * logging out is the machinery, and a failed login legitimately returns 401
 * without meaning the session ended. `/auth/me` is intentionally NOT here — it's
 * how a session is restored, so it should refresh-and-retry like any other call.
 */
function isRefreshExemptPath(path: string): boolean {
  return (
    path.startsWith('/auth/login') ||
    path.startsWith('/auth/refresh') ||
    path.startsWith('/auth/logout')
  )
}

/**
 * Outcome of rotating the refresh token.
 * - `ok` — new pair stored; retry the original request.
 * - `invalid` — refresh token rejected (401/403); session is over.
 * - `unavailable` — network / 5xx (e.g. deploy); keep tokens, do not sign out.
 */
export type RefreshOutcome = 'ok' | 'invalid' | 'unavailable'

/**
 * A single in-flight refresh shared across concurrent 401s, so a burst of
 * requests failing at once triggers exactly one token rotation.
 */
let refreshInFlight: Promise<RefreshOutcome> | null = null

async function performRefresh(): Promise<RefreshOutcome> {
  const refreshToken = tokenStorage.getRefresh()
  if (!refreshToken) return 'invalid'
  try {
    const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    })
    // Definitive auth rejection only — not deploy blips (502/503/504).
    if (response.status === 401 || response.status === 403) {
      logger.warn('Token refresh rejected', { status: response.status })
      return 'invalid'
    }
    if (!response.ok) {
      logger.warn('Token refresh unavailable', { status: response.status })
      return 'unavailable'
    }
    const parsed = validate(loginResponseDtoSchema, parseJson(await response.text()), 'POST', '/auth/refresh')
    tokenStorage.setSession(parsed.accessToken, parsed.refreshToken)
    sessionEvents.emitAccessTokenRotated(parsed.accessToken)
    return 'ok'
  } catch (error) {
    logger.warn('Token refresh failed (network)', { error: toErrorMessage(error) })
    return 'unavailable'
  }
}

/**
 * Rotate the session once, however many callers — and however many tabs — ask.
 *
 * A refresh token is single-use, and presenting a spent one is how the API
 * recognizes a stolen one: it revokes every session the person has, on every
 * device. Tabs share one token through localStorage, so two refreshing at once
 * — a deploy drops every live floor-plan socket at the same moment, and each
 * tab's retry is refused together — would sign the person out everywhere.
 * Tabs take turns under a browser lock, and a tab that finds the token already
 * rotated by the time its turn comes uses the new one instead of spending the
 * old one a second time.
 */
function ensureRefreshed(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    const presented = tokenStorage.getRefresh()
    refreshInFlight = underRefreshLock(async () => {
      const current = tokenStorage.getRefresh()
      if (current !== null && current !== presented) return 'ok'
      return performRefresh()
    }).finally(() => {
      refreshInFlight = null
    })
  }
  return refreshInFlight
}

/** One refresh at a time across this origin's tabs, where the browser can say so. */
async function underRefreshLock(refresh: () => Promise<RefreshOutcome>): Promise<RefreshOutcome> {
  if (!('locks' in navigator)) return refresh()
  // Typed as the callback's promise; the lock resolves with what it resolves to.
  const outcome = await navigator.locks.request('cse.session-refresh', refresh)
  return outcome
}

/**
 * Renew the session after the access token was refused: by a request, on a
 * 401, or by the live floor-plan socket, whose sign-in is refused once the
 * token has expired. A refresh token that is refused too ends the session.
 *
 * The socket used to read only the stored token, so a tab left in the
 * background past the token's hour went on offering the refused one every
 * couple of seconds for as long as it stayed open.
 */
export async function renewSession(): Promise<RefreshOutcome> {
  const outcome = await ensureRefreshed()
  if (outcome === 'invalid') {
    tokenStorage.clear()
    sessionEvents.emitSignedOut()
  }
  return outcome
}

/** True when the failure is likely temporary (deploy, network), not a signed-out user. */
export function isTransientApiError(error: unknown): boolean {
  if (!isApiError(error)) return true
  return error.status === 0 || error.status === 502 || error.status === 503 || error.status === 504 || error.status >= 500
}

/**
 * Perform the HTTP request, applying the base URL, auth header, and JSON body
 * encoding, and translating a failed response into a thrown {@link ApiError}.
 * Returns the raw response text so each method can parse it with its schema.
 *
 * On a 401 with a stored refresh token, the access token is silently rotated
 * (once, shared across concurrent calls) and the request retried. The session is
 * cleared only when refresh is **definitively** rejected — not on network/5xx
 * (so deploys do not force sign-out).
 */
async function send(
  method: string,
  path: string,
  body: RequestBody | undefined,
  options: RequestOptions | undefined,
  allowRefresh = true,
): Promise<string> {
  const headers: Record<string, string> = { ...options?.headers }
  if (body !== undefined && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const token = options?.anonymous === true ? null : tokenStorage.get()
  if (token) headers['Authorization'] = `Bearer ${token}`

  let response: Response
  try {
    response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(options?.signal ? { signal: options.signal } : {}),
    })
  } catch (error) {
    // Rethrow caller-initiated cancellation so callers can detect it.
    if (error instanceof Error && error.name === 'AbortError') throw error
    const message = toErrorMessage(error)
    // A breadcrumb, like every other failure here: the service reports it a
    // moment later and can say what was being attempted. Logging at error level
    // filed a second issue for the same dropped connection.
    logger.warn('Network request failed', { path: loggablePath(path), error: message })
    throw new ApiError(0, { message })
  }

  const text = await response.text()

  if (!response.ok) {
    if (response.status === 401 && options?.anonymous === true) {
      // Nothing to refresh and no session to end: this call never had one.
    } else if (response.status === 401 && !isRefreshExemptPath(path) && allowRefresh) {
      // Refresh token invalid/expired: renewSession ends the session.
      const outcome = await renewSession()
      if (outcome === 'ok') {
        return send(method, path, body, options, false)
      }
      if (outcome === 'unavailable') {
        // API likely mid-deploy — keep tokens so the user can retry without re-login.
        logger.warn('Auth refresh unavailable; keeping session', { path })
        throw new ApiError(503, {
          message: 'Service temporarily unavailable. Your session was kept — try again in a moment.',
        })
      }
    } else if (response.status === 401 && !isRefreshExemptPath(path) && !allowRefresh) {
      // Retry after refresh still 401, or no refresh attempted and already retried.
      tokenStorage.clear()
      sessionEvents.emitSignedOut()
    }
    const errorBody = readErrorBody(text)
    if (response.status === 403 && errorBody.code === API_ERROR_CODE.billingLocked) {
      sessionEvents.emitBillingLocked()
    }
    const detail = {
      path: loggablePath(path),
      status: response.status,
      message: errorBody.message ?? errorBody.error ?? null,
    }
    // A breadcrumb, never an issue. The service that made this call reports the
    // failure a moment later, and it can say what was being attempted — "Failed
    // to save event phase" rather than a path and a number. Recording the
    // transport detail first means it is already attached when that issue is
    // filed; both logging at error level filed the same failure twice, under the
    // less useful of the two titles.
    logger.warn('API request failed', detail)
    throw new ApiError(response.status, errorBody)
  }

  return text
}

export const api = {
  /** GET a resource that is always present; validates the body with `schema`. */
  async get<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('GET', path, undefined, options)
    return validate(schema, parseJson(text), 'GET', path)
  },

  /**
   * GET a resource that may be absent. An empty body / 204 / bare `null`
   * resolves to `null`; otherwise the body is validated with `schema`.
   */
  async getNullable<T>(
    path: string,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T | null> {
    const text = await send('GET', path, undefined, options)
    const parsed = parseJson(text)
    if (parsed === null) return null
    return validate(schema, parsed, 'GET', path)
  },

  /** POST a JSON body and validate the response with `schema`. */
  async post<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('POST', path, body, options)
    return validate(schema, parseJson(text), 'POST', path)
  },

  /** PUT a JSON body and validate the response with `schema`. */
  async put<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('PUT', path, body, options)
    return validate(schema, parseJson(text), 'PUT', path)
  },

  /** PATCH a JSON body and validate the response with `schema`. */
  async patch<T>(
    path: string,
    body: RequestBody,
    schema: ZodType<T>,
    options?: RequestOptions,
  ): Promise<T> {
    const text = await send('PATCH', path, body, options)
    return validate(schema, parseJson(text), 'PATCH', path)
  },

  /** DELETE a resource. Resolves once the server confirms; no body is returned. */
  async del(path: string, options?: RequestOptions): Promise<void> {
    await send('DELETE', path, undefined, options)
  },

  /**
   * DELETE a resource that answers with a summary of what it did (e.g. which
   * accounts went with it); validates the body with `schema`.
   */
  async delFor<T>(path: string, schema: ZodType<T>, options?: RequestOptions): Promise<T> {
    const text = await send('DELETE', path, undefined, options)
    return validate(schema, parseJson(text), 'DELETE', path)
  },

  /**
   * DELETE with a JSON body: undoing an act that takes a note, such as reopening
   * a lost inquiry. Validates the response with `schema`.
   */
  async delWith<T>(path: string, body: RequestBody, schema: ZodType<T>, options?: RequestOptions): Promise<T> {
    const text = await send('DELETE', path, body, options)
    return validate(schema, parseJson(text), 'DELETE', path)
  },
}
